import React, { useEffect, useState } from 'react';
import { getWrongQuestions, WrongQuestionBookData } from '../api/placement';
import { questionToHtml } from '../utils/richText';

/**
 * Wrong-question book (错题本, ROO-160): still-wrong questions grouped by
 * knowledge point. Answering a question correctly later removes it. Each group
 * links into targeted practice for that skill; the header links to the
 * recommended set built from these wrong answers.
 */

export interface WrongQuestionBookProps {
  studentId: string;
  onBack: () => void;
  onPractiseRecommended: () => void;
  onPractiseSkill: (skill: string) => void;
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1.5px solid #EFE6D0',
  borderRadius: 16,
  padding: '18px 20px',
  marginBottom: 14,
};

const WrongQuestionBook: React.FC<WrongQuestionBookProps> = ({
  studentId,
  onBack,
  onPractiseRecommended,
  onPractiseSkill,
}) => {
  const [data, setData] = useState<WrongQuestionBookData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getWrongQuestions(studentId)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch((err) => {
        if (alive) {
          setError(err?.response?.data?.error || 'Failed to load your tricky questions.');
          setLoading(false);
        }
      });
    return () => { alive = false; };
  }, [studentId]);

  return (
    <div className="cvr-portal" style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px 48px' }}>
      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
        <button
          type="button"
          onClick={onBack}
          style={{ border: '1.5px solid #E5DCC3', background: '#fff', color: '#1A2A1F', borderRadius: 999, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          ← Dashboard
        </button>
        {data && data.totalWrong > 0 && (
          <button
            type="button"
            onClick={onPractiseRecommended}
            style={{ border: 0, background: '#14593C', color: '#fff', borderRadius: 999, padding: '8px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}
          >
            Practise these →
          </button>
        )}
      </div>

      <h1 style={{ margin: '10px 0 4px', fontSize: 24, fontWeight: 800, color: '#1A2A1F' }}>Tricky questions</h1>
      <p style={{ margin: '0 0 18px', fontSize: 14, color: '#6B7A6F' }}>
        Questions you haven&rsquo;t cracked yet, grouped by skill. Get one right in practice and it disappears from here.
      </p>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6B7A6F' }}>Loading…</div>}
      {error && <div style={{ padding: 20, color: '#B83E24', fontSize: 14 }}>{error}</div>}

      {data && data.totalWrong === 0 && (
        <section style={{ ...cardStyle, textAlign: 'center', padding: '38px 20px' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🎉</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1A2A1F', marginBottom: 4 }}>No tricky questions!</div>
          <div style={{ fontSize: 13.5, color: '#6B7A6F' }}>
            Everything you&rsquo;ve missed, you&rsquo;ve since got right. Keep practising to stay sharp.
          </div>
        </section>
      )}

      {data && data.groups.map((g) => (
        <section key={g.skill} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1A2A1F' }}>
              {g.label}
              <span style={{ marginLeft: 8, fontSize: 12.5, fontWeight: 700, color: '#B83E24', background: '#FAD5C8', borderRadius: 999, padding: '2px 10px' }}>
                {g.questions.length}
              </span>
            </h2>
            <button
              type="button"
              onClick={() => onPractiseSkill(g.skill)}
              style={{ border: '1.5px solid #5DAA3A', background: '#DCEFCC', color: '#14593C', borderRadius: 999, padding: '5px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
            >
              Practise {g.label} →
            </button>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {g.questions.map((q) => {
              const html = questionToHtml(q.tableContent ? { tableContent: q.tableContent } : {}, q.question);
              return (
                <li key={q.id} style={{ padding: '10px 0', borderTop: '1px solid #F3EEE0', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                  <div style={{ flex: 1, fontSize: 13.5, color: '#1A2A1F', minWidth: 0 }}>
                    {html ? (
                      <span dangerouslySetInnerHTML={{ __html: html }} />
                    ) : (
                      <span>{q.question}</span>
                    )}
                  </div>
                  <span style={{ flexShrink: 0, fontSize: 11.5, color: '#9AA79E', whiteSpace: 'nowrap' }}>
                    missed {q.timesWrong}×
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default WrongQuestionBook;
